// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {createVoiceAudioContext} from '@app/features/voice/engine/VoiceSharedAudioContext';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {DeepFilterNet3Core, DeepFilterNoiseFilterProcessor, getAssetLoader} from 'deepfilternet3-noise-filter';
import type {NoiseSuppressionStage} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionChain';
import type {LocalAudioTrack} from 'livekit-client';

const logger = new Logger('DeepFilterNoiseProcessor');
const DEEP_FILTER_MODEL_SAMPLE_RATE = 48000;
const DEFAULT_SUPPRESSION_LEVEL = 80;
const HIGH_PASS_FREQUENCY_HZ = 60;
const HIGH_PASS_Q = Math.SQRT1_2;
const LIMITER_THRESHOLD_DB = -3;
const LIMITER_KNEE_DB = 0;
const LIMITER_RATIO = 20;
const LIMITER_ATTACK_SEC = 0.003;
const LIMITER_RELEASE_SEC = 0.05;

let activeProcessor: DeepFilterNoiseFilterProcessor | null = null;
let activeTrack: LocalAudioTrack | null = null;
let deepFilterAssetsPreload: Promise<void> | null = null;

function resolveDeepFilterAssetCdnUrl(): string | null {
	const endpoint = RuntimeConfig.staticCdnEndpoint?.trim();
	if (!endpoint) return null;
	return `${endpoint.replace(/\/$/, '')}/libs/deepfilternet3`;
}

export function createDeepFilterProcessor(
	noiseReductionLevel = DEFAULT_SUPPRESSION_LEVEL,
): DeepFilterNoiseFilterProcessor {
	const clampedNoiseReductionLevel = Math.max(0, Math.min(100, noiseReductionLevel));
	return new DeepFilterNoiseFilterProcessor({
		sampleRate: DEEP_FILTER_MODEL_SAMPLE_RATE,
		noiseReductionLevel: clampedNoiseReductionLevel,
		enabled: true,
		assetConfig: {
			cdnUrl: resolveDeepFilterAssetCdnUrl() ?? `${RuntimeConfig.staticCdnEndpoint}/libs/deepfilternet3`,
		},
	});
}

/**
 * Warm DeepFilterNet3 WASM + ONNX archive in HTTP/browser cache before voice join.
 * Join still constructs its own processor; this only removes the cold network stall.
 */
export async function preloadDeepFilterAssets(signal?: AbortSignal): Promise<void> {
	signal?.throwIfAborted();
	const cdnUrl = resolveDeepFilterAssetCdnUrl();
	if (!cdnUrl) {
		logger.debug('DeepFilter asset preload skipped: static CDN endpoint not ready');
		return;
	}

	if (deepFilterAssetsPreload) {
		await deepFilterAssetsPreload;
		signal?.throwIfAborted();
		return;
	}

	const pending = (async () => {
		const startedAt = performance.now();
		const loader = getAssetLoader({cdnUrl});
		const urls = loader.getAssetUrls();
		const [wasmResponse, modelResponse] = await Promise.all([
			fetch(urls.wasm, {signal}),
			fetch(urls.model, {signal}),
		]);
		if (!wasmResponse.ok) {
			throw new Error(`DeepFilter WASM preload failed with status ${wasmResponse.status}`);
		}
		if (!modelResponse.ok) {
			throw new Error(`DeepFilter model preload failed with status ${modelResponse.status}`);
		}
		const [wasmBytes, modelBytes] = await Promise.all([wasmResponse.arrayBuffer(), modelResponse.arrayBuffer()]);
		signal?.throwIfAborted();
		if (wasmBytes.byteLength === 0 || modelBytes.byteLength === 0) {
			throw new Error('DeepFilter preload returned an empty asset');
		}
		await WebAssembly.compile(wasmBytes.slice(0));
		logger.info('DeepFilter assets preloaded', {
			wasmBytes: wasmBytes.byteLength,
			modelBytes: modelBytes.byteLength,
			elapsedMs: Math.round((performance.now() - startedAt) * 10) / 10,
		});
	})();

	deepFilterAssetsPreload = pending.catch((error) => {
		deepFilterAssetsPreload = null;
		throw error;
	});
	await deepFilterAssetsPreload;
}

export function resolveDeepFilterAudioContext(
	sourceContext: AudioContext,
	feedTrack: MediaStreamTrack,
): AudioContext | null {
	const modelRateContext = createVoiceAudioContext({
		latencyHint: 'interactive',
		sampleRate: DEEP_FILTER_MODEL_SAMPLE_RATE,
	});
	if (!modelRateContext) return null;
	if (modelRateContext.sampleRate === sourceContext.sampleRate) return modelRateContext;
	try {
		modelRateContext.createMediaStreamSource(new MediaStream([feedTrack])).disconnect();
		return modelRateContext;
	} catch (error) {
		logger.info('DeepFilter cannot read the capture graph at the model rate; running it at the capture rate', {
			modelSampleRate: modelRateContext.sampleRate,
			captureSampleRate: sourceContext.sampleRate,
			error,
		});
	}
	void modelRateContext.close().catch((error) => {
		logger.debug('Failed to close the rejected DeepFilter AudioContext', error);
	});
	return createVoiceAudioContext({latencyHint: 'interactive', sampleRate: sourceContext.sampleRate});
}

export interface DeepFilterAudioChain {
	processedTrack: MediaStreamTrack;
	inputDestination: MediaStreamAudioDestinationNode;
	dispose: () => Promise<void>;
}

function safeDisconnect(node: AudioNode | null | undefined): void {
	if (!node) return;
	try {
		node.disconnect();
	} catch {}
}

function safeStopTrack(track: MediaStreamTrack | null | undefined): void {
	if (!track) return;
	try {
		track.stop();
	} catch {}
}

export async function buildDeepFilterAudioChain(opts: {
	audioContext: AudioContext;
	noiseReductionLevel?: number;
}): Promise<DeepFilterAudioChain> {
	const {audioContext} = opts;
	const noiseReductionLevel = Math.max(
		0,
		Math.min(100, opts.noiseReductionLevel ?? VoiceSettings.getDeepFilterNoiseSuppressionLevel()),
	);
	const inputDestination = audioContext.createMediaStreamDestination();
	const inputTrack = inputDestination.stream.getAudioTracks()[0];
	if (!inputTrack) {
		throw new Error('buildDeepFilterAudioChain: missing input destination track');
	}
	const hpfSource = audioContext.createMediaStreamSource(new MediaStream([inputTrack]));
	const highPass = audioContext.createBiquadFilter();
	highPass.type = 'highpass';
	highPass.frequency.value = HIGH_PASS_FREQUENCY_HZ;
	highPass.Q.value = HIGH_PASS_Q;
	hpfSource.connect(highPass);
	const deepFilterFeedDestination = audioContext.createMediaStreamDestination();
	highPass.connect(deepFilterFeedDestination);
	const deepFilterFeedTrack = deepFilterFeedDestination.stream.getAudioTracks()[0];
	if (!deepFilterFeedTrack) {
		safeDisconnect(hpfSource);
		safeDisconnect(highPass);
		safeDisconnect(inputDestination);
		safeDisconnect(deepFilterFeedDestination);
		safeStopTrack(inputTrack);
		throw new Error('buildDeepFilterAudioChain: missing DeepFilter feed track');
	}
	const processor = createDeepFilterProcessor(noiseReductionLevel);
	processor.audioContext = resolveDeepFilterAudioContext(audioContext, deepFilterFeedTrack);
	const disposeDeepFilterInputGraph = () => {
		safeDisconnect(inputDestination);
		safeDisconnect(hpfSource);
		safeDisconnect(highPass);
		safeDisconnect(deepFilterFeedDestination);
		safeStopTrack(inputTrack);
		safeStopTrack(deepFilterFeedTrack);
	};
	try {
		await processor.init({track: deepFilterFeedTrack});
	} catch (error) {
		disposeDeepFilterInputGraph();
		try {
			await processor.destroy();
		} catch (destroyError) {
			logger.debug('DeepFilter destroy after init failure threw', destroyError);
		}
		throw error;
	}
	if (!processor.processedTrack) {
		disposeDeepFilterInputGraph();
		try {
			await processor.destroy();
		} catch (destroyError) {
			logger.debug('DeepFilter destroy after missing processedTrack threw', destroyError);
		}
		throw new Error('DeepFilter init produced no processedTrack');
	}
	const limiterSource = audioContext.createMediaStreamSource(new MediaStream([processor.processedTrack]));
	const limiter = audioContext.createDynamicsCompressor();
	limiter.threshold.value = LIMITER_THRESHOLD_DB;
	limiter.knee.value = LIMITER_KNEE_DB;
	limiter.ratio.value = LIMITER_RATIO;
	limiter.attack.value = LIMITER_ATTACK_SEC;
	limiter.release.value = LIMITER_RELEASE_SEC;
	limiterSource.connect(limiter);
	const outputDestination = audioContext.createMediaStreamDestination();
	limiter.connect(outputDestination);
	const processedTrack = outputDestination.stream.getAudioTracks()[0];
	if (!processedTrack) {
		disposeDeepFilterInputGraph();
		safeDisconnect(limiterSource);
		safeDisconnect(limiter);
		safeDisconnect(outputDestination);
		safeStopTrack(processor.processedTrack);
		try {
			await processor.destroy();
		} catch (destroyError) {
			logger.debug('DeepFilter destroy after missing output track threw', destroyError);
		}
		throw new Error('buildDeepFilterAudioChain: missing limiter output track');
	}
	let disposed = false;
	const dispose = async () => {
		if (disposed) return;
		disposed = true;
		safeDisconnect(inputDestination);
		safeDisconnect(hpfSource);
		safeDisconnect(highPass);
		safeDisconnect(deepFilterFeedDestination);
		safeDisconnect(limiterSource);
		safeDisconnect(limiter);
		safeDisconnect(outputDestination);
		try {
			await processor.destroy();
		} catch (error) {
			logger.warn('Failed to destroy DeepFilter processor in chain dispose', error);
		}
		safeStopTrack(inputTrack);
		safeStopTrack(deepFilterFeedTrack);
		safeStopTrack(processor.processedTrack);
		safeStopTrack(processedTrack);
	};
	return {
		processedTrack,
		inputDestination,
		dispose,
	};
}

export async function applyDeepFilterProcessor(
	track: LocalAudioTrack,
	noiseReductionLevel = VoiceSettings.getDeepFilterNoiseSuppressionLevel(),
): Promise<void> {
	if (!VoiceSettings.getDeepFilterNoiseSuppression()) {
		return;
	}
	try {
		await removeDeepFilterProcessor();
		const processor = createDeepFilterProcessor(noiseReductionLevel);
		await track.setProcessor(processor);
		activeTrack = track;
		activeProcessor = processor;
		logger.info('Applied DeepFilterNet3 noise suppression');
	} catch (error) {
		logger.warn('Failed to apply DeepFilterNet3 noise suppression', error);
		activeTrack = null;
		activeProcessor = null;
	}
}

export async function removeDeepFilterProcessor(track?: LocalAudioTrack): Promise<void> {
	if (activeProcessor) {
		const targetTrack = activeTrack ?? track;
		try {
			if (targetTrack) {
				await targetTrack.stopProcessor();
			}
		} catch (error) {
			logger.warn('Failed to stop DeepFilter processor', error);
		}
		try {
			await activeProcessor.destroy();
		} catch (error) {
			logger.warn('Failed to destroy DeepFilter processor', error);
		}
		activeTrack = null;
		activeProcessor = null;
		logger.debug('Removed DeepFilterNet3 noise suppression');
	}
}

export function setDeepFilterEnabled(enabled: boolean): void {
	if (activeProcessor) {
		const result = activeProcessor.setEnabled(enabled);
		void Promise.resolve(result).catch((error) => {
			logger.warn('Failed to set DeepFilter enabled state', error);
		});
		logger.debug('Set DeepFilter enabled', {enabled});
	}
}

export function isDeepFilterActive(): boolean {
	return activeProcessor != null;
}

export function isDeepFilterAppliedToTrack(track?: LocalAudioTrack | null): boolean {
	return activeProcessor != null && activeTrack === (track ?? null);
}

/**
 * DeepFilterNet3 as a plain node (HPF → DFN worklet → limiter) inside its own 48 kHz context, so the voice
 * input graph runs in a single AudioContext without MediaStream bridges. DFN does heavy per-10 ms inference
 * on the audio thread; a 20 ms render buffer absorbs its spikes instead of glitching under CPU pressure.
 */
export async function buildDeepFilterStage(opts: {
	captureTrack: MediaStreamTrack;
	noiseReductionLevel?: number;
}): Promise<NoiseSuppressionStage> {
	const noiseReductionLevel = Math.max(
		0,
		Math.min(100, opts.noiseReductionLevel ?? VoiceSettings.getDeepFilterNoiseSuppressionLevel()),
	);
	const context = createVoiceAudioContext({latencyHint: 0.02, sampleRate: DEEP_FILTER_MODEL_SAMPLE_RATE});
	if (!context) throw new Error('DeepFilter stage: no audio context');
	const core = new DeepFilterNet3Core({
		sampleRate: DEEP_FILTER_MODEL_SAMPLE_RATE,
		noiseReductionLevel,
		assetConfig: {cdnUrl: resolveDeepFilterAssetCdnUrl() ?? `${RuntimeConfig.staticCdnEndpoint}/libs/deepfilternet3`},
	});
	const highPass = context.createBiquadFilter();
	const limiter = context.createDynamicsCompressor();
	let node: AudioWorkletNode | null = null;
	let disposed = false;
	const dispose = async () => {
		if (disposed) return;
		disposed = true;
		safeDisconnect(highPass);
		safeDisconnect(node);
		safeDisconnect(limiter);
		try {
			core.destroy();
		} catch (error) {
			logger.debug('DeepFilter core destroy threw', error);
		}
		if (context.state !== 'closed') await context.close().catch(() => undefined);
	};
	try {
		// Probe: the raw capture track must be readable at the model rate.
		context.createMediaStreamSource(new MediaStream([opts.captureTrack])).disconnect();
		if (context.state === 'suspended') {
			// Without a recent user gesture resume() can hang; give up quickly and let the caller fall back.
			await Promise.race([
				context.resume(),
				new Promise((_, reject) => window.setTimeout(() => reject(new Error('DeepFilter context resume timed out')), 1500)),
			]);
		}
		await core.initialize();
		node = await core.createAudioWorkletNode(context);
		highPass.type = 'highpass';
		highPass.frequency.value = HIGH_PASS_FREQUENCY_HZ;
		highPass.Q.value = HIGH_PASS_Q;
		limiter.threshold.value = LIMITER_THRESHOLD_DB;
		limiter.knee.value = LIMITER_KNEE_DB;
		limiter.ratio.value = LIMITER_RATIO;
		limiter.attack.value = LIMITER_ATTACK_SEC;
		limiter.release.value = LIMITER_RELEASE_SEC;
		highPass.connect(node);
		node.connect(limiter);
		logger.info('DeepFilter stage ready', {sampleRate: context.sampleRate, noiseReductionLevel});
		return {context, input: highPass, output: limiter, dispose};
	} catch (error) {
		await dispose();
		throw error;
	}
}
